/**
 * Table utility functions for battery map rendering
 * Provides reusable logic for table construction, unit placement, and alignment
 */

// Unit shape constants
export const UNIT_SHAPE = {
	SQUARE: 0,
	HORIZONTAL: 1,
	VERTICAL: 2
};

// Rotation constants
export const ROTATION = {
	NONE: 0,
	TOP_LEFT: 1,
	TOP_RIGHT: 2,
	BOTTOM_LEFT: 3,
	BOTTOM_RIGHT: 4
};

/**
 * Initialize an empty table grid
 * @param {number} rows - Number of rows
 * @param {number} columns - Number of columns
 * @returns {Array} - 2D array filled with null
 */
export const createEmptyTable = (rows, columns) => {
	const table = [];
	for (let i = 0; i < rows; i++) {
		table[i] = new Array(columns).fill(null);
	}
	return table;
};

/**
 * Alignment configuration based on rotation value
 * @param {number} rotation - Rotation value (0-4)
 * @returns {Object} - Vertical and horizontal alignment
 */
export const getAlignment = (rotation) => {
	switch (rotation) {
		case ROTATION.TOP_LEFT:
			return { vertical: 'top', horizontal: 'left' };
		case ROTATION.TOP_RIGHT:
			return { vertical: 'top', horizontal: 'right' };
		case ROTATION.BOTTOM_LEFT:
			return { vertical: 'bottom', horizontal: 'left' };
		case ROTATION.BOTTOM_RIGHT:
			return { vertical: 'bottom', horizontal: 'right' };
		default:
			return { vertical: 'center', horizontal: 'center' };
	}
};

/**
 * Transform unit shape based on rotation
 * @param {number} originalShape - Original unit shape (0=square, 1=horizontal, 2=vertical)
 * @param {number} rotation - Rotation value
 * @returns {number} - Transformed shape
 */
export const transformUnitShape = (originalShape, rotation) => {
	// Square units are unaffected by rotation
	if (originalShape === UNIT_SHAPE.SQUARE || rotation === ROTATION.NONE) {
		return originalShape;
	}
	
	// Horizontal becomes vertical, vertical becomes horizontal
	return originalShape === UNIT_SHAPE.HORIZONTAL ? UNIT_SHAPE.VERTICAL : UNIT_SHAPE.HORIZONTAL;
};

/**
 * Determine if the table needs to be expanded
 * @param {Array} units - Array of unit objects
 * @param {number} unitShape - Base unit shape
 * @param {number} numberColumns - Current number of columns
 * @param {number} numberRows - Current number of rows
 * @returns {Object} - Expansion requirements
 */
export const calculateTableExpansion = (units, unitShape, numberColumns, numberRows) => {
	let needsTopRow = false;
	let needsRightColumn = false;
	let needsBottomRow = false;
	let needsLeftColumn = false;

	units.forEach(unit => {
		const rotation = unit.unit_rotation || ROTATION.NONE;
		
		// Skip square units or units with no rotation
		if (unitShape === UNIT_SHAPE.SQUARE || rotation === ROTATION.NONE) return;

		// Horizontal units rotated can span up or down
		if (unitShape === UNIT_SHAPE.HORIZONTAL) {
			// Top rotations span upward
			if ((rotation === ROTATION.TOP_LEFT || rotation === ROTATION.TOP_RIGHT) && unit.unit_position_row === 0) {
				needsTopRow = true;
			}
			// Bottom rotations span downward
			if ((rotation === ROTATION.BOTTOM_LEFT || rotation === ROTATION.BOTTOM_RIGHT) && unit.unit_position_row === numberRows - 1) {
				needsBottomRow = true;
			}
		}

		// Vertical units rotated can span left or right
		if (unitShape === UNIT_SHAPE.VERTICAL) {
			// Right rotations span rightward
			if ((rotation === ROTATION.TOP_RIGHT || rotation === ROTATION.BOTTOM_RIGHT) && unit.unit_position_col === numberColumns - 1) {
				needsRightColumn = true;
			}
			// Left rotations span leftward
			if ((rotation === ROTATION.TOP_LEFT || rotation === ROTATION.BOTTOM_LEFT) && unit.unit_position_col === 0) {
				needsLeftColumn = true;
			}
		}
	});

	// Calculate table expansion and offset
	const rowExpansion = (needsTopRow ? 1 : 0) + (needsBottomRow ? 1 : 0);
	const colExpansion = (needsLeftColumn ? 1 : 0) + (needsRightColumn ? 1 : 0);
	const rowOffset = needsTopRow ? 1 : 0;
	const colOffset = needsLeftColumn ? 1 : 0;

	return { rowExpansion, colExpansion, rowOffset, colOffset };
};

// Calculate cell spanning for a unit
export const calculateCellSpan = ({
	originalShape,
	rotation,
	row,
	col,
	actualRows,
	actualColumns,
	occupied
}) => {
	let rowspan = 1;
	let colspan = 1;
	let startRow = row;
	let startCol = col;

	// Only apply spanning if there's rotation or Square battery shape
	if (rotation === ROTATION.NONE || originalShape === UNIT_SHAPE.SQUARE) {
		return { rowspan, colspan, startRow, startCol };
	}

	// Horizontal units with rotation become vertical and span vertically
	if (originalShape === UNIT_SHAPE.HORIZONTAL) {
		// Top rotations span upward
		if (rotation === ROTATION.TOP_LEFT || rotation === ROTATION.TOP_RIGHT) {
			const targetRow = row - 1;
			if (targetRow >= 0 && !occupied[targetRow][col]) {
				rowspan = 2;
				startRow = targetRow;
			}
		}
		// Bottom rotations span downward
		else if (rotation === ROTATION.BOTTOM_LEFT || rotation === ROTATION.BOTTOM_RIGHT) {
			const targetRow = row + 1;
			if (targetRow < actualRows && !occupied[targetRow][col]) {
				rowspan = 2;
			}
		}
	}
	
	// Vertical units with rotation become horizontal and span horizontally
	else if (originalShape === UNIT_SHAPE.VERTICAL) {
		// Right rotations span rightward
		if (rotation === ROTATION.TOP_RIGHT || rotation === ROTATION.BOTTOM_RIGHT) {
			const targetCol = col + 1;
			if (targetCol < actualColumns && !occupied[row][targetCol]) {
				colspan = 2;
			}
		}
		// Left rotations span leftward
		else if (rotation === ROTATION.TOP_LEFT || rotation === ROTATION.BOTTOM_LEFT) {
			const targetCol = col - 1;
			if (targetCol >= 0 && !occupied[row][targetCol]) {
				colspan = 2;
				startCol = targetCol;
			}
		}
	}

	return { rowspan, colspan, startRow, startCol };
};

/**
 * Mark cells as occupied in the occupancy grid
 * @param {Array} occupied - 2D occupancy grid
 * @param {number} row - Starting row
 * @param {number} col - Starting column
 * @param {number} rowspan - Number of rows to span
 * @param {number} colspan - Number of columns to span
 */
export const markCellsOccupied = (occupied, row, col, rowspan, colspan) => {
	for (let r = row; r < row + rowspan; r++) {
		for (let c = col; c < col + colspan; c++) {
			occupied[r][c] = true;
		}
	}
};

/**
 * Create a unit object for table placement
 * @param {Object} unit - Unit data from API
 * @param {Object} params - Additional parameters
 * @returns {Object} - Unit object for rendering
 */
export const createUnitObject = (unit, { shape, rowspan, colspan, rotation }) => {
	return {
		unit_number: unit.unit_number,
		unit_shape: shape,
		unit_status: unit.status,
		unit_dates: {
			install: unit.install_date,
			manufacture: unit.manufacture_date,
			warranty_expiration: unit.warranty_expiration_date,
		},
		unit_comments: unit.unit_comments,
		rowspan,
		colspan,
		alignment: getAlignment(rotation)
	};
};

/**
 * Mark spanned cells in the table
 * @param {Array} table - 2D table array
 * @param {number} row - Starting row
 * @param {number} col - Starting column
 * @param {number} rowspan - Number of rows to span
 * @param {number} colspan - Number of columns to span
 */
export const markSpannedCells = (table, row, col, rowspan, colspan) => {
	for (let r = row; r < row + rowspan; r++) {
		for (let c = col; c < col + colspan; c++) {
			if (r !== row || c !== col) {
				table[r][c] = { isSpanned: true };
			}
		}
	}
};

/**
 * Sort units for optimal table placement
 * Non-rotated units are processed first to claim their base positions,
 * then rotated units are processed to prevent placement conflicts
 * @param {Array} units - Array of unit objects
 * @returns {Array} - Sorted copy of units array
 */
export const sortUnitsForPlacement = (units) => {
	return [...units].sort((a, b) => {
		const aRotation = a.unit_rotation || 0;
		const bRotation = b.unit_rotation || 0;
		
		// Non-rotated units first (they don't span)
		if (aRotation === ROTATION.NONE && bRotation !== ROTATION.NONE) return -1;
		if (aRotation !== ROTATION.NONE && bRotation === ROTATION.NONE) return 1;
		
		// Then sort by row, then column
		if (a.unit_position_row !== b.unit_position_row) {
			return a.unit_position_row - b.unit_position_row;
		}
		return a.unit_position_col - b.unit_position_col;
	});
};

/**
 * Build a complete unit table with rotation and spanning logic
 * @param {Object} params - Table building parameters
 * @returns {Array} - 2D array representing the table
 */
export const buildUnitTable = ({ unitShape, numberColumns, numberRows, units }) => {
	// If dimensions are missing, return empty table
	if (!numberColumns || !numberRows) {
		return [];
	}

	// If no units, return an initialized empty table with correct dimensions
	if (!units || units.length === 0) {
		return createEmptyTable(numberRows, numberColumns);
	}

	// Calculate table expansion needs
	const { rowExpansion, colExpansion, rowOffset, colOffset } = calculateTableExpansion(
		units,
		unitShape,
		numberColumns,
		numberRows
	);

	// Calculate actual table dimensions
	const actualRows = rowExpansion + numberRows;
	const actualColumns = colExpansion + numberColumns;

	// Initialize table and occupancy grid
	const table = createEmptyTable(actualRows, actualColumns);
	const occupied = Array(actualRows).fill(null).map(() => Array(actualColumns).fill(false));

	// Sort units to process non-spanning units first, then spanning units
	const sortedUnits = sortUnitsForPlacement(units);

	// Place each unit in the table
	sortedUnits.forEach(unit => {
		const rotation = unit.unit_rotation || 0;
		const shape = transformUnitShape(unitShape, rotation);
		
		// Calculate base position with offsets
		const baseRow = unit.unit_position_row + rowOffset;
		const baseCol = unit.unit_position_col + colOffset;

		// Calculate spanning
		const { rowspan, colspan, startRow, startCol } = calculateCellSpan({
			originalShape: unitShape,
			rotation,
			row: baseRow,
			col: baseCol,
			actualRows,
			actualColumns,
			occupied
		});

		// Create unit object
		const unitObj = createUnitObject(unit, { shape, rowspan, colspan, rotation });

		// Mark cells as occupied
		markCellsOccupied(occupied, startRow, startCol, rowspan, colspan);

		// Place unit in table
		table[startRow][startCol] = unitObj;
		
		// Mark spanned cells
		markSpannedCells(table, startRow, startCol, rowspan, colspan);
	});

	return table;
};
